import { IconToggle } from '@modules/common/components/IconToggle/IconToggle';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useCopyAssistantResponseMarkdownAsHTML } from '@modules/oracle/hooks';
import { OraclePrompt } from '@modules/oracle/store/types';
import { trackWisdomEvents } from '@modules/oracle/utils';
import { ContentPasteOutlined } from '@mui/icons-material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { IconButton, IconButtonProps, Tooltip } from '@mui/material';
import { useState } from 'react';

interface CopyMarkdownButtonProps extends IconButtonProps {
  prompt: OraclePrompt;
}

export function CopyMarkdownButton({
  prompt,
  ...iconButtonProps
}: CopyMarkdownButtonProps) {
  const [shouldToggleCopyIcon, setShouldToggleCopyIcon] = useState(false);
  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const { markdownToCopy, isLoading } = useCopyAssistantResponseMarkdownAsHTML({
    assistantResponseTokenV2List: prompt.assistantResponse?.answer?.tokensV2,
  });

  const copyMarkdown = async () => {
    try {
      const markdown = await markdownToCopy();
      showSuccessNotification({
        contentComponent: 'Content copied to clipboard.',
        props: {},
      });

      setShouldToggleCopyIcon(true);
      trackWisdomEvents({
        eventName: 'Wisdom - Copy Response Clicked',
        responseID: prompt.id,
        response: markdown,
      });
    } catch (e) {
      showErrorNotification({
        contentComponent: 'Failed to copy to clipboard.',
        props: {},
      });
    }
  };

  const renderCopyIcon = () => {
    return (
      <Tooltip title={isLoading ? 'Loading...' : 'Copy to clipboard'}>
        <IconButton
          {...iconButtonProps}
          color="primary"
          size="small"
          aria-label="copy"
          onClick={copyMarkdown}
          disabled={isLoading}
        >
          <ContentPasteOutlined fontSize="inherit" />
        </IconButton>
      </Tooltip>
    );
  };

  const renderCopiedIcon = () => {
    return (
      <Tooltip title="Copied">
        <IconButton
          {...iconButtonProps}
          color="success"
          size="small"
          aria-label="copied"
          disableRipple
        >
          <CheckRounded fontSize="inherit" />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <IconToggle
      defaultIcon={renderCopyIcon()}
      toggledIcon={renderCopiedIcon()}
      isToggled={shouldToggleCopyIcon}
      onToggle={setShouldToggleCopyIcon}
    />
  );
}
